<template>
  <div class="delivery-info-section">
    <h6 class="delivery-info-section__title">
      Pharmacy Delivery Info
    </h6>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Store Name*</label>
      <SanitizedInput
        v-model="info.storeName"
        :disabled="disabled"
        placeholder="Please input name of store"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Store Phone Number</label>
      <phone-number-input
        id="pharmacy-store-phone"
        v-model="info.storePhoneNumber"
        :disabled="disabled"
        :show-live-call="false"
      />
      <div class="delivery-info-section__help">
        Leave empty to fall back to the caller's phone number.
      </div>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Is the store open?*</label>
      <el-select
        v-model="info.storeOpen"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in yesNoOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Medication Name*</label>
      <SanitizedTextarea
        v-model="info.medicationName"
        :disabled="disabled"
        rows="3"
        placeholder="Please input name of medication"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Controlled Substance*</label>
      <el-select
        v-model="info.controlledSubstance"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in yesNoOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Paid For*</label>
      <el-select
        v-model="info.paidFor"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in yesNoOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Item Count*</label>
      <el-input-number
        v-model="info.itemCount"
        :disabled="disabled"
        :min="1"
        :step="1"
        controls-position="right"
        class="delivery-info-section__control"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Patient Name*</label>
      <SanitizedInput
        v-model="info.patientName"
        :disabled="disabled"
        placeholder="Please input name of patient"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Date of Birth*</label>
      <div class="delivery-info-section__dob">
        <DropdownList
          v-model="dobMonth"
          clearable
          placeholder="Month"
          :disabled="disabled"
        >
          <template #additional-options-prefix>
            <el-option
              v-for="month in 12"
              :key="month"
              :label="month"
              :value="month"
            >
              {{ month }}
            </el-option>
          </template>
        </DropdownList>
        <DropdownList
          v-model="dobDay"
          clearable
          filterable
          placeholder="Day"
          :disabled="disabled"
        >
          <template #additional-options-prefix>
            <el-option
              v-for="day in 31"
              :key="day"
              :label="day"
              :value="day"
            >
              {{ day }}
            </el-option>
          </template>
        </DropdownList>
        <DropdownList
          v-model="dobYear"
          clearable
          filterable
          placeholder="Year"
          :disabled="disabled"
        >
          <template #additional-options-prefix>
            <el-option
              v-for="year in yearList"
              :key="year"
              :label="year"
              :value="year"
            >
              {{ year }}
            </el-option>
          </template>
        </DropdownList>
      </div>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Pickup Instructions</label>
      <SanitizedTextarea
        v-model="info.pickupInstructions"
        :disabled="disabled"
        rows="3"
        placeholder="Explain how we will need to pick up the item"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Delivery Options*</label>
      <el-select
        v-model="info.deliveryOptions"
        :disabled="disabled"
        placeholder="Select"
        class="delivery-info-section__control"
      >
        <el-option
          v-for="option in specialRequestOrPharmacyDeliveryOptions"
          :key="option.key"
          :label="option.label"
          :value="option.key"
        />
      </el-select>
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Delivery Instructions</label>
      <SanitizedTextarea
        v-model="info.deliveryInstructions"
        :disabled="disabled"
        rows="3"
        placeholder="Explain how we will need to deliver the item"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Driver Tip($)</label>
      <el-input-number
        v-model="info.driverTipDollars"
        :disabled="disabled"
        :min="0"
        :step="0.01"
        :precision="2"
        controls-position="right"
        class="delivery-info-section__control"
      />
    </div>

    <div class="delivery-info-section__field">
      <label class="delivery-info-section__label">Notes for LP</label>
      <SanitizedTextarea
        v-model="info.notesForLP"
        :disabled="disabled"
        rows="3"
        placeholder="Additional notes for Loss & Prevention"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import mtz from 'moment-timezone';

import { maxAge, minAge } from '@/constants/caller';
import { yesNoOptions } from '@/constants/app';
import { specialRequestOrPharmacyDeliveryOptions } from '@/constants/ride';
import { YesNoEnum } from '@/models/app';
import { RideServiceType, type DeliveryEstimatePayload, type DeliveryInfoFormResult, type PharmacyDeliveryInfo } from '@/models/ride';
import {
  buildSharedDeliveryEstimatePayload,
  type DeliveryValidationResult,
} from '@/Utils/deliveryInfoForm';
import DropdownList from '@/components/UIComponents/DropdownList.vue';

const info = defineModel<PharmacyDeliveryInfo>({ required: true });

withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  (event: 'update:result', value: DeliveryInfoFormResult): void;
}>();

const dobMonth = ref<number | null>(null);
const dobDay = ref<number | null>(null);
const dobYear = ref<number | null>(null);

const yearList = computed(() => {
  const minYear = new Date().getFullYear() - maxAge;
  const maxYear = new Date().getFullYear() - minAge;
  return new Array(maxYear - minYear).fill(minYear).map((year: number, ind: number) => year + ind + 1);
});

const partsToDate = (): Date | null => {
  if (!dobYear.value || !dobMonth.value || !dobDay.value) {
    return null;
  }

  const date = new Date(dobYear.value, dobMonth.value - 1, dobDay.value);
  if (
    date.getFullYear() !== dobYear.value ||
    date.getMonth() !== dobMonth.value - 1 ||
    date.getDate() !== dobDay.value
  ) {
    return null;
  }

  if (mtz().subtract(minAge, 'years').isBefore(mtz(date))) {
    return null;
  }

  return date;
};

const isFilledString = (value: string | null | undefined): boolean => Boolean(value && value.trim());

const getValidationErrors = (info: PharmacyDeliveryInfo): Record<string, string> => {
  const nextErrors: Record<string, string> = {};

  if (!isFilledString(info.storeName)) {
    nextErrors.storeName = 'Store Name is required';
  }
  if (!info.storeOpen) {
    nextErrors.storeOpen = 'Store open status is required';
  } else if (info.storeOpen !== YesNoEnum.Yes) {
    nextErrors.storeOpen = 'The store must be open to place this delivery';
  }
  if (!isFilledString(info.medicationName)) {
    nextErrors.medicationName = 'Medication Name is required';
  }
  if (!info.controlledSubstance) {
    nextErrors.controlledSubstance = 'Controlled Substance is required';
  } else if (info.controlledSubstance !== YesNoEnum.No) {
    nextErrors.controlledSubstance = 'Controlled substances cannot be delivered';
  }
  if (!info.paidFor) {
    nextErrors.paidFor = 'Paid For is required';
  } else if (info.paidFor !== 'Yes') {
    nextErrors.paidFor = 'The medication must be paid for before delivery';
  }
  if (!info.itemCount || info.itemCount < 1) {
    nextErrors.itemCount = 'Item Count must be at least 1';
  }
  if (!isFilledString(info.patientName)) {
    nextErrors.patientName = 'Patient Name is required';
  }
  if (!info.birthday) {
    nextErrors.birthday = 'Date of Birth is required';
  }
  if (!info.deliveryOptions) {
    nextErrors.deliveryOptions = 'Delivery Options is required';
  }

  return nextErrors;
};

const buildEstimatePayload = (info: PharmacyDeliveryInfo): DeliveryEstimatePayload | null => {
  const shared = buildSharedDeliveryEstimatePayload({ info, deliveryType: RideServiceType.PharmacyDelivery });
  if (!shared || !info.birthday) {
    return null;
  }

  return {
    ...shared,
    medicationName: info.medicationName.trim(),
    isControlledSubstance: info.controlledSubstance === YesNoEnum.Yes,
    patientName: info.patientName.trim(),
    patientDateOfBirth: mtz(info.birthday).format('YYYY-MM-DD'),
  };
};

const buildResult = (info: PharmacyDeliveryInfo): DeliveryInfoFormResult => {
  const validationErrors = getValidationErrors(info);
  const isValid = Object.keys(validationErrors).length === 0;

  return {
    isValid,
    payload: isValid ? buildEstimatePayload(info) : null,
    notesForLP: info.notesForLP,
  };
};

const result = computed(() => buildResult(info.value));

const validate = (): DeliveryValidationResult => {
  const errors = Object.values(getValidationErrors(info.value));
  emit('update:result', result.value);
  return {
    isValid: errors.length === 0,
    errors,
  };
};

watch([dobMonth, dobDay, dobYear], () => {
  info.value.birthday = partsToDate();
});

watch(
  () => info.value.birthday,
  (birthday) => {
    if (!birthday) {
      return;
    }

    const year = birthday.getFullYear();
    const month = birthday.getMonth() + 1;
    const day = birthday.getDate();
    if (dobYear.value !== year || dobMonth.value !== month || dobDay.value !== day) {
      dobYear.value = year;
      dobMonth.value = month;
      dobDay.value = day;
    }
  },
  { immediate: true },
);

watch(
  result,
  (value) => {
    emit('update:result', value);
  },
  { immediate: true, deep: true },
);

defineExpose({
  validate,
});
</script>

<style lang="scss" scoped>
.delivery-info-section__dob {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 420px;

  :deep(.el-select) {
    flex: 1;
    min-width: 0;
  }
}
</style>
